import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { MdArrowBack, MdDelete, MdDownload } from "react-icons/md";
import { FaUpload } from "react-icons/fa";

import Hoc from "../../../components/HOC";
import Loader from "../../../components/Loader";
import SimpleTable from "../../../components/SimpleTable";
import CustomToolTip from "../../../components/ToolTip";

import CandidateDocumentModal from "./CandidateDocumentModal";

import {
  deleteCandidateDocument,
  downloadCandidateDocument,
  getCandidateDocuments,
  uploadCandidateDocument,
} from "../../../Services/candidateDocuments";

const documentsList = [
  { label: "Passport", value: "PASSPORT" },
  { label: "Visa", value: "VISA" },
  { label: "Driving Licence", value: "DRIVING_LICENCE" },
  { label: "SSN", value: "SSN" },
  { label: "Resume", value: "RESUME" },
  { label: "Other", value: "OTHER" },
];

const initialDocumentValues = {
  documentType: "",
  file: null,
  issueDate: "",
  expiryDate: "",
  others: "",
};

const CandidateDocuments = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [candidateName, setCandidateName] = useState("");

  const candidateId = useMemo(() => Number(id), [id]);

  const fetchDocuments = useCallback(async () => {
    if (!candidateId || candidateId === 0) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await getCandidateDocuments(candidateId);
      const filteredDocuments = data.filter((doc) => Boolean(doc.fileName));
      setDocuments([...filteredDocuments].reverse());
      
      // Extract candidateName from API response (present in each document)
      if (data && Array.isArray(data) && data.length > 0) {
        const name = data[0].candidateName || data.find(doc => doc.candidateName)?.candidateName;
        if (name) {
          setCandidateName(name);
        }
      }
    } catch (error) {
      setDocuments([]);
      toast.error(error?.message || "Failed to load candidate documents");
    } finally {
      setIsLoading(false);
    }
  }, [candidateId]);

    

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUploadDocument = async (values) => {
    if (!candidateId || candidateId === 0) {
      toast.error("Candidate information missing.");
      return;
    }

    const documentType =
      values.documentType === "OTHER"
        ? values.others?.trim().toUpperCase()
        : values.documentType;

    try {
      setIsLoading(true);
      await uploadCandidateDocument(
        candidateId,
        documentType,
        values.issueDate,
        values.expiryDate,
        values.file
      );
      toast.success("Document uploaded successfully");
      setShowModal(false);
      fetchDocuments();
    } catch (error) {
      toast.error(error?.message || "Failed to upload document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDocument = async (row) => {
    if (!candidateId || candidateId === 0) {
      toast.error("Candidate information missing.");
      return;
    }

    try {
      setIsLoading(true);
      await deleteCandidateDocument(candidateId, row.documentType);
      toast.success("Document deleted successfully");
      fetchDocuments();
    } catch (error) {
      toast.error(error?.message || "Failed to delete document");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadDocument = async (row) => {
    try {
      let base64Data = row.downloadLink;
      let filename = row.originalFilename || row.documentType || "document";

      if (!base64Data) {
        const downloadResponse = await downloadCandidateDocument(row.fileName);
        base64Data = downloadResponse?.base64Data || "";
        if (downloadResponse?.originalFilename) {
          filename = downloadResponse.originalFilename;
        }
      }

      if (!base64Data) {
        toast.error("Download link not available");
        return;
      }

      const link = window.document.createElement("a");
      link.href = base64Data;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      toast.error(error?.message || "Failed to download document");
    }
  };


  const columns = [
    {
      name: "Document Type",
      selector: "documentType",
      renderCell: (row) => row.documentType || "N/A",
    },
    {
      name: "Issue Date",
      selector: "issueDate",
      renderCell: (row) =>
        row.issueDate
          ? moment(row.issueDate).format("MM-DD-YYYY")
          : "Not Applicable",
    },
    {
      name: "Expiry Date",
      selector: "expiryDate",
      renderCell: (row) =>
        row.expiryDate
          ? moment(row.expiryDate).format("MM-DD-YYYY")
          : "Not Applicable",
    },
    {
      name: "Actions",
      selector: "actions",
      renderCell: (row) => (
        <div className="flex items-center justify-center space-x-4">
          <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDocument(row);
              }}
            className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <>
              <MdDelete size={20} data-tip data-for={`Delete-${row.id}`} />
              <CustomToolTip id={`Delete-${row.id}`} title="Delete" />
            </>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadDocument(row);
            }}
            className="text-[#201F1E] hover:text-[#201F1E] p-2 rounded-full hover:bg-gray-200 transition-colors"
          >
            <>
              <MdDownload size={20} data-tip data-for={`Download-${row.id}`} />
              <CustomToolTip id={`Download-${row.id}`} title="Download" />
            </>
          </button>
        </div>
      ),
      headerClassName: "text-center",
    },
  ];

  if (!candidateId || candidateId === 0) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Invalid candidate ID</p>
          <button
            onClick={() => navigate("/candidates")}
            className="px-4 py-2 bg-[#201F1E] text-white rounded-lg hover:bg-[#2b2a29]"
          >
            Back to Candidates
          </button>
        </div>
      </div>
    );
  }
  

  return (
    <div className="bg-white min-h-screen">
      <Loader isLoading={isLoading} />

      <div className="w-full px-4 py-6 flex flex-col">
        <div className="mb-6 w-full rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
            <h2 className="order-1 text-center text-lg font-semibold leading-snug text-[#201F1E] sm:order-none sm:col-start-2 sm:row-start-1 sm:px-2 sm:text-xl min-w-0">
              <span className="block text-xs font-medium uppercase tracking-wide text-[#656464] sm:text-sm">
                Documents
              </span>
              <span className="mt-0.5 block">
                Upload for{" "}
                <span className="break-words font-semibold text-[#201F1E]">
                  {candidateName || "Candidate"}
                </span>
              </span>
            </h2>

            <div className="order-2 flex items-center justify-between gap-3 sm:contents">
              <button
                type="button"
                onClick={() => navigate("/candidates")}
                className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] border border-[#D1D1D1] bg-white px-4 text-sm font-medium text-[#201F1E] transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#201F1E]/20 focus:ring-offset-1 sm:col-start-1 sm:row-start-1"
              >
                <MdArrowBack className="mr-2" size={20} /> Back
              </button>
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] bg-[#201F1E] px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#2b2a29] focus:outline-none focus:ring-2 focus:ring-[#201F1E]/30 focus:ring-offset-1 sm:col-start-3 sm:row-start-1"
              >
                <FaUpload className="mr-2" size={16} />
                Upload Document
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <SimpleTable columns={columns} data={documents} scrollHeight="60vh" />
        </div>
      </div>

      <CandidateDocumentModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Upload Candidate Document"
        initialValues={initialDocumentValues}
        documentsList={documentsList}
        submitForm={handleUploadDocument}
      />
    </div>
  );
};

export default Hoc(CandidateDocuments);
